import forge from 'apis/forge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSceneComments, selectUserSettings } from 'redux/db/reducers';
import { setActiveCommentId } from 'redux/mode/actions';
import { selectTransform } from 'redux/mode/reducers';
import {
	getVisibleCanvasCenterInSceneCoordinates,
	navigateToComment,
	openCanvasLeft,
	openCanvasWidth,
} from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

const SAVE_DEBOUNCE_MS = 1000;

const CommentStickyNote = () => {
	const dispatch = useDispatch();

	const {
		comments,
		transform,
		canvasHeight,
		canvasLeft,
		visibleWidth,
		activeCommentId,
		activeSceneChatId,
		commentEditingEnabled,
	} = useSelector(
		(r: RootReducer) => ({
			comments: selectSceneComments(r.db),
			transform: selectTransform(r.mode),
			canvasHeight: r.mode.canvasHeight,
			canvasLeft: openCanvasLeft(r.mode),
			visibleWidth: openCanvasWidth(r.mode),
			activeCommentId: r.mode.activeCommentId,
			activeSceneChatId: r.mode.activeSceneChatId,
			commentEditingEnabled: selectUserSettings(r.db).commentEditingEnabled,
		}),
		shallowEqual
	);

	const activeCommentIndex = useMemo(
		() => comments.findIndex((c) => c.id === activeCommentId),
		[comments, activeCommentId]
	);

	const activeComment = activeCommentIndex >= 0 ? comments[activeCommentIndex] : null;

	const [editText, setEditText] = useState('');
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingSaveRef = useRef<(() => void) | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const autoResizeTextarea = useCallback(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.overflow = 'hidden';
		const maxH = 400;
		if (el.scrollHeight > maxH) {
			el.style.height = `${maxH}px`;
			el.style.overflow = 'auto';
		} else {
			el.style.height = `${el.scrollHeight}px`;
		}
	}, []);

	// Sync local edit text when active comment changes
	useEffect(() => {
		if (activeComment) {
			setEditText(activeComment.text);
		}
		if (pendingSaveRef.current) {
			pendingSaveRef.current();
			pendingSaveRef.current = null;
		}
		if (saveTimerRef.current) {
			clearTimeout(saveTimerRef.current);
			saveTimerRef.current = null;
		}
	}, [activeCommentId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Flush pending save on unmount
	useEffect(() => {
		return () => {
			if (pendingSaveRef.current) {
				pendingSaveRef.current();
			}
			if (saveTimerRef.current) {
				clearTimeout(saveTimerRef.current);
			}
		};
	}, []);

	// Auto-resize textarea to fit content
	useEffect(() => {
		autoResizeTextarea();
	}, [editText, activeCommentId, autoResizeTextarea]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onNavigate = useCallback(
		(direction: 'prev' | 'next') => {
			if (comments.length === 0) return;
			let idx = activeCommentIndex;
			if (direction === 'next') {
				idx = idx < 0 ? 0 : (idx + 1) % comments.length;
			} else {
				idx = idx < 0 ? comments.length - 1 : (idx - 1 + comments.length) % comments.length;
			}
			navigateToComment(comments[idx]);
		},
		[comments, activeCommentIndex]
	);

	const onDelete = useCallback(async () => {
		if (!activeCommentId) return;
		dispatch(setActiveCommentId(null));
		try {
			await forge.deleteSceneComment(activeCommentId, true);
		} catch {
			// forge already rolls back the optimistic write and shows a toast
		}
	}, [activeCommentId, dispatch]);

	const onClose = useCallback(() => {
		dispatch(setActiveCommentId(null));
	}, [dispatch]);

	// Capture current screen center, zoom, and chat scroll state into the comment's metadata + position
	const onUpdate = useCallback(() => {
		if (!activeComment) return;
		const center = getVisibleCanvasCenterInSceneCoordinates();
		const zoom = transform.scale;
		const chatId = activeSceneChatId ?? null;
		const chatContainer = document.querySelector(
			'[data-chat-scroll-container]'
		) as HTMLElement | null;
		const chatScrollTop = chatContainer?.scrollTop;
		const chatScrollHeight = chatContainer?.scrollHeight;
		forge.updateSceneComment({
			id: activeComment.id,
			x: Math.round(center.x),
			y: Math.round(center.y),
			metadata: {
				...activeComment.metadata,
				zoom,
				chatId,
				chatScrollTop,
				chatScrollHeight,
			},
		});
	}, [activeComment, transform.scale, activeSceneChatId]);

	const onTextChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value;
			setEditText(value);
			if (!activeCommentId) return;
			const commentId = activeCommentId;

			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			const save = () => {
				pendingSaveRef.current = null;
				forge.updateSceneComment({ id: commentId, text: value });
			};
			pendingSaveRef.current = save;
			saveTimerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
		},
		[activeCommentId]
	);

	// Escape closes; left/right arrows navigate (only when textarea not focused)
	useEffect(() => {
		if (!activeCommentId) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				dispatch(setActiveCommentId(null));
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				const tag = document.activeElement?.tagName;
				if (tag === 'TEXTAREA' || tag === 'INPUT') return;
				e.preventDefault();
				onNavigate(e.key === 'ArrowLeft' ? 'prev' : 'next');
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [activeCommentId, dispatch, onNavigate]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (!activeComment) return null;

	const screenX = transform.scale * activeComment.x + transform.offset.x;
	const screenY = transform.scale * activeComment.y + transform.offset.y;
	const noteWidth = 280;
	const rightBound = canvasLeft + visibleWidth - noteWidth - 16;
	const left = Math.max(canvasLeft + 16, Math.min(screenX + 20, rightBound));
	const top = Math.max(0, Math.min(screenY - 20, (canvasHeight || 600) - 200));

	return (
		<S.StickyNote style={{ left, top }}>
			<S.StickyHeader>
				<S.HeaderLeft>
					<S.NavArrow onClick={() => onNavigate('prev')}>&#x25C0;</S.NavArrow>
					<S.NavLabel>
						{activeCommentIndex + 1}/{comments.length}
					</S.NavLabel>
					<S.NavArrow onClick={() => onNavigate('next')}>&#x25B6;</S.NavArrow>
				</S.HeaderLeft>
				<S.HeaderRight>
					{commentEditingEnabled && (
						<>
							<S.HeaderButton onClick={onUpdate}>Update</S.HeaderButton>
							<S.HeaderButton onClick={onDelete}>Delete</S.HeaderButton>
						</>
					)}
					<S.HeaderButton onClick={onClose}>&#x2715;</S.HeaderButton>
				</S.HeaderRight>
			</S.StickyHeader>
			<S.EditableText
				ref={textareaRef}
				value={editText}
				onChange={onTextChange}
				readOnly={!commentEditingEnabled}
				data-undo-exempt="true"
			/>
		</S.StickyNote>
	);
};

export default CommentStickyNote;
